// src/utils/image.ts
export type WatermarkPosition =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'center';

export interface WatermarkOptions {
  // Lado maior do watermark como fração da largura da imagem base (0-1). Ex.: 0.25 = 25%
  relativeWidth?: number;
  // Posição do watermark
  position?: WatermarkPosition;
  // Opacidade do watermark (0-1)
  opacity?: number;
  // Margem em px nas bordas
  margin?: number;
  // Qualidade do JPEG (0-1)
  quality?: number;
  // Redimensionamento opcional da imagem base antes de aplicar watermark
  maxWidth?: number;
  maxHeight?: number;
  // Forçar retorno como File com este nome (mantém .jpg)
  returnFileName?: string;
  // Força CORS anônimo ao carregar imagens via URL (requer CORS habilitado no servidor)
  crossOriginAnonymous?: boolean;
  // Quando true, centraliza o watermark dentro do quadrante escolhido (ignora margin)
  centerInQuadrant?: boolean;
}

export type RotationDegrees = 90 | 180 | 270 | -90;
export type FlipDirection = 'horizontal' | 'vertical';

type ImageInput = File | Blob | string | HTMLImageElement;

/**
 * Converte uma imagem para JPEG e aplica uma marca d’água.
 * - `image`: arquivo/Blob/URL/dataURL/HTMLImageElement
 * - `watermarkSrc`: caminho/URL do arquivo do watermark (ex.: "/Imagens/watermark.png")
 * - `options`: ver WatermarkOptions
 *
 * Retorna Blob por padrão; se `returnFileName` for informado, retorna File.
 */
export async function applyWatermarkAndConvertToJpeg(
  image: ImageInput,
  watermarkSrc: string,
  options: WatermarkOptions = {}
): Promise<File> {
  const {
    relativeWidth = 0.25,
    position = 'bottom-right',
    opacity = 0.5,
    margin = 16,
    quality = 0.9,
    maxWidth,
    maxHeight,
    returnFileName,
    crossOriginAnonymous = true,
    centerInQuadrant = false
  } = options;

  const baseImg = await loadHTMLImage(image, crossOriginAnonymous);
  const { canvas, ctx } = createCanvasFitting(baseImg, maxWidth, maxHeight);

  // Desenha a imagem base (já redimensionada se maxWidth/maxHeight)
  ctx.drawImage(baseImg, 0, 0, canvas.width, canvas.height);

  // Carrega watermark
  const wmImg = await loadHTMLImage(watermarkSrc, crossOriginAnonymous);

  // Calcula tamanho do watermark com base na largura da imagem base
  const targetW = Math.max(1, Math.round(canvas.width * relativeWidth));
  const scale = targetW / wmImg.naturalWidth;
  const targetH = Math.max(1, Math.round(wmImg.naturalHeight * scale));

  // Calcula posição
  const { x, y } = placeWatermark(
    position,
    canvas.width,
    canvas.height,
    targetW,
    targetH,
    margin,
    centerInQuadrant
  );

  // Desenha watermark com opacidade
  const prevAlpha = ctx.globalAlpha;
  ctx.globalAlpha = clamp01(opacity);
  ctx.drawImage(wmImg, x, y, targetW, targetH);
  ctx.globalAlpha = prevAlpha;

  // Exporta como JPEG
  const blob = await canvasToJpeg(canvas, quality);

  // Always return File
  const fileName = ensureJpegExtension(
    returnFileName ?? deriveNameFromImageInput(image) ?? 'image'
  );

  return new File([blob], fileName, { type: 'image/jpeg' });
}

// Convenience alias remains, now just forwards strongly-typed File
export async function applyWatermarkAndConvertToJpegFile(
  image: ImageInput,
  watermarkSrc: string,
  fileName: string,
  options: Omit<WatermarkOptions, 'returnFileName'> = {}
): Promise<File> {
  return applyWatermarkAndConvertToJpeg(image, watermarkSrc, {
    ...options,
    returnFileName: fileName
  });
}

/**
 * Rotaciona uma imagem e exporta como JPEG.
 * - 90  : 90° horário
 * - -90 / 270 : 90° anti-horário
 * - 180 : meia-volta
 */
export async function rotateImage(
  image: ImageInput,
  degrees: RotationDegrees,
  options: Pick<
    WatermarkOptions,
    'quality' | 'returnFileName' | 'crossOriginAnonymous'
  > = {}
): Promise<File> {
  const {
    quality = 0.9,
    returnFileName,
    crossOriginAnonymous = true
  } = options;

  const baseImg = await loadHTMLImage(image, crossOriginAnonymous);
  const srcW = baseImg.naturalWidth || baseImg.width;
  const srcH = baseImg.naturalHeight || baseImg.height;

  // 90 / -90 / 270 trocam largura e altura
  const swap = degrees === 90 || degrees === -90 || degrees === 270;
  const canvasW = swap ? srcH : srcW;
  const canvasH = swap ? srcW : srcH;

  const canvas = document.createElement('canvas');
  canvas.width = canvasW;
  canvas.height = canvasH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context not available');

  const rad = ((degrees % 360) * Math.PI) / 180;
  ctx.translate(canvasW / 2, canvasH / 2);
  ctx.rotate(rad);
  ctx.drawImage(baseImg, -srcW / 2, -srcH / 2, srcW, srcH);

  const blob = await canvasToJpeg(canvas, quality);
  const fileName = ensureJpegExtension(
    returnFileName ?? deriveNameFromImageInput(image) ?? 'image'
  );
  return new File([blob], fileName, { type: 'image/jpeg' });
}

/**
 * Espelha uma imagem horizontalmente ou verticalmente e exporta como JPEG.
 */
export async function flipImage(
  image: ImageInput,
  direction: FlipDirection,
  options: Pick<
    WatermarkOptions,
    'quality' | 'returnFileName' | 'crossOriginAnonymous'
  > = {}
): Promise<File> {
  const {
    quality = 0.9,
    returnFileName,
    crossOriginAnonymous = true
  } = options;

  const baseImg = await loadHTMLImage(image, crossOriginAnonymous);
  const w = baseImg.naturalWidth || baseImg.width;
  const h = baseImg.naturalHeight || baseImg.height;

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context not available');

  if (direction === 'horizontal') {
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
  } else {
    ctx.translate(0, h);
    ctx.scale(1, -1);
  }
  ctx.drawImage(baseImg, 0, 0, w, h);

  const blob = await canvasToJpeg(canvas, quality);
  const fileName = ensureJpegExtension(
    returnFileName ?? deriveNameFromImageInput(image) ?? 'image'
  );
  return new File([blob], fileName, { type: 'image/jpeg' });
}

// Helpers for naming
function deriveNameFromImageInput(image: ImageInput): string {
  if (image instanceof File) return image.name;
  if (typeof image === 'string') return basenameFromString(image);
  // Blob or HTMLImageElement without a name → default
  return 'image';
}

function basenameFromString(src: string): string {
  if (src.startsWith('data:')) return 'image';
  try {
    const u = new URL(src, window.location.origin);
    const last = u.pathname.split('/').pop() ?? 'image';
    return last || 'image';
  } catch {
    const path = src.split('?')[0].split('#')[0];
    const parts = path.split('/');
    return parts.pop() || 'image';
  }
}

function clamp01(n: number) {
  if (Number.isNaN(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

function ensureJpegExtension(name: string) {
  return name.toLowerCase().endsWith('.jpg') ||
    name.toLowerCase().endsWith('.jpeg')
    ? name
    : `${name}.jpg`;
}

function createCanvasFitting(
  img: HTMLImageElement,
  maxWidth?: number,
  maxHeight?: number
) {
  let w = img.naturalWidth || img.width;
  let h = img.naturalHeight || img.height;

  if (maxWidth || maxHeight) {
    const mw = maxWidth ?? w;
    const mh = maxHeight ?? h;
    const ratio = Math.min(mw / w, mh / h, 1);
    w = Math.max(1, Math.round(w * ratio));
    h = Math.max(1, Math.round(h * ratio));
  }

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context not available');
  return { canvas, ctx };
}

function placeWatermark(
  position: WatermarkPosition,
  baseW: number,
  baseH: number,
  wmW: number,
  wmH: number,
  margin: number,
  centerInQuadrant = false
) {
  if (centerInQuadrant) {
    const halfW = baseW / 2;
    const halfH = baseH / 2;
    switch (position) {
      case 'top-left':
        return {
          x: Math.round((halfW - wmW) / 2),
          y: Math.round((halfH - wmH) / 2)
        };
      case 'top-right':
        return {
          x: Math.round(halfW + (halfW - wmW) / 2),
          y: Math.round((halfH - wmH) / 2)
        };
      case 'bottom-left':
        return {
          x: Math.round((halfW - wmW) / 2),
          y: Math.round(halfH + (halfH - wmH) / 2)
        };
      case 'bottom-right':
      default:
        return {
          x: Math.round(halfW + (halfW - wmW) / 2),
          y: Math.round(halfH + (halfH - wmH) / 2)
        };
      case 'center':
        return {
          x: Math.round((baseW - wmW) / 2),
          y: Math.round((baseH - wmH) / 2)
        };
    }
  }

  switch (position) {
    case 'top-left':
      return { x: margin, y: margin };
    case 'top-right':
      return { x: baseW - wmW - margin, y: margin };
    case 'bottom-left':
      return { x: margin, y: baseH - wmH - margin };
    case 'center':
      return {
        x: Math.round((baseW - wmW) / 2),
        y: Math.round((baseH - wmH) / 2)
      };
    case 'bottom-right':
    default:
      return { x: baseW - wmW - margin, y: baseH - wmH - margin };
  }
}

async function canvasToJpeg(canvas: HTMLCanvasElement, quality: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Failed to export JPEG'))),
      'image/jpeg',
      clamp01(quality)
    );
  });
}

async function loadHTMLImage(
  src: ImageInput,
  crossOriginAnonymous: boolean
): Promise<HTMLImageElement> {
  if (src instanceof HTMLImageElement) {
    // Já carregada?
    if (src.complete && src.naturalWidth > 0) return src;
    await waitForImage(src);
    return src;
  }

  let url: string | null = null;
  let revoke: (() => void) | null = null;

  try {
    if (src instanceof Blob) {
      url = URL.createObjectURL(src);
      revoke = () => URL.revokeObjectURL(url!);
    } else if (typeof src === 'string') {
      url = src;
    } else {
      // fallback genérico (deve cobrir File/Blob)
      url = URL.createObjectURL(src as Blob);
      revoke = () => URL.revokeObjectURL(url!);
    }

    const img = new Image();
    if (crossOriginAnonymous) img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.src = url!;
    await waitForImage(img);
    return img;
  } finally {
    // Não revoga aqui; precisamos do recurso até desenhar.
    // O chamador não reutiliza a URL; o GC cuidará. Se for crítico, exponha uma API para revogar depois.
  }
}

function waitForImage(img: HTMLImageElement) {
  return new Promise<void>((resolve, reject) => {
    const clean = () => {
      img.onload = null;
      img.onerror = null;
    };
    img.onload = () => {
      clean();
      resolve();
    };
    img.onerror = (e) => {
      clean();
      reject(new Error('Failed to load image'));
    };
  });
}
